import moment from 'moment';
import { v4 as uuidV4 } from 'uuid';
import config from '../config.json';
import constants from '../constants/tokenErrorConstants';
/* eslint-disable-next-line import/no-cycle */
import loginActions from './loginActionCreators';
import notificationActions from './notificationActionCreators';
import logger from '../utils/logger';
import { getUser } from '../components/common/classes/permissions';
import { getItem, setItem } from '../utils/localStorage';
import browserLocation from '../utils/browserLocation';
const increment = () => ({
  type: constants.INCREMENT
});
const postError = function postError(label, message) {
  let additionalAction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : () => {};
  let id = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : uuidV4();
  return dispatch => {
    dispatch(notificationActions.error({
      message,
      rightButtonLabel: label,
      rightButtonAction: additionalAction,
      id // keep it so only one shows up at a time
    }, {
      // don't allow user to close the window without accepting
      autoClose: false,
      closeButton: false
    }));
  };
};
const reset = () => ({
  type: constants.RESET
});
const retryOnSuccess = retryAction => ({
  type: constants.RETRY_ON_SUCCESS,
  retryAction
});

/**
 * @method handleError
 *
 * @param {object} error data from the server
 * @param {string} entity name of the redux entity calling the method
 * @param {function} failureAction bound redux action, fired to signify a failure of intended action
 * @param {function} retryAction bound redux action, dispatched on TOKEN_SUCCESS (optional)
 */
const handleError = (error, entity, failureAction, retryAction, handleRedirect) => async (dispatch, getState) => {
  var _error$response;
  const state = getState().tokenError;

  // handle expired access_token
  if (((_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.status) === 401) {
    // If no retryAction was provided, refresh the token but do not queue a retry.
    // This is important for non-idempotent operations where replaying
    // could have unintended effects.
    if (!retryAction) {
      try {
        await dispatch(loginActions.serverToken());
      } catch (refreshError) {
        logger("Token refresh failed without retryAction: ".concat(refreshError.message || refreshError));
        dispatch(postError('Okay', 'Your session could not be refreshed. Please try again or sign in again if the problem persists.'));
        dispatch(reset());
        dispatch(failureAction());
        return null;
      }
      dispatch(failureAction());
      return null;
    }

    // add action for retry on tokenSuccess and increment the retryCount
    dispatch(retryOnSuccess(retryAction));
    dispatch(increment());
    if (state.retryCount < state.maxTokenRetries) {
      await dispatch(loginActions.serverToken());
    } else {
      // Retries exhausted - clear the queue and dispatch failure
      dispatch(reset());
      dispatch(failureAction());
    }
  } else if (error.response) {
    // handle outdated client version (403)
    if (error.response.status === 403) {
      // Updating the web app is a 2 step process. First, the browser needs to be refreshed to get
      // the latest code from the CDN. Once that's complete, the access token needs to be updated

      let forceLogout = false;
      let forceRefresh = true;
      if (config.version === getUser().client_version) {
        // refresh to get latest code. The code and access token versions match, but the server
        // sent back an upgrade message. Try to reload the browser to get the latest.
        // Use localStorage to only reload once
        const forcedRefreshDate = getItem('forcedRefreshDate');
        let forcedRefreshDuration;
        if (forcedRefreshDate) {
          const forceDate = moment(forcedRefreshDate);
          forcedRefreshDuration = moment.duration(moment().diff(forceDate));
        }
        if (!forcedRefreshDuration || forcedRefreshDuration.asMinutes() > 5) {
          setItem('forcedRefreshDate', moment().toISOString());
        } else if (forcedRefreshDuration.asSeconds() < 10) {
          // multiple network requests will all send 403s. Ignore all except the first
          forceRefresh = false;
        } else {
          // the user has waited at least 10 seconds and found no update, force the user to logout
          forceLogout = true;
          forceRefresh = false;
        }
      } else {
        // code and access token version mismatch, get an updated token. This state should be
        // reached after the first 403 refreshes the browser
        forceRefresh = false;
        const upgradeTokenDateStr = getItem('upgradeTokenDate');
        let upgradeTokenDuration;
        if (upgradeTokenDateStr) {
          const upgradeDate = moment(upgradeTokenDateStr);
          upgradeTokenDuration = moment.duration(moment().diff(upgradeDate));
        }
        if (!upgradeTokenDuration || upgradeTokenDuration.asMinutes() > 5) {
          // old or missing item, request a new token
          setItem('upgradeTokenDate', moment().toISOString());
          dispatch(loginActions.serverToken());

          // unfortunately, the browser has been reloaded so `retryActions` is cleared, force
          // another refresh
          const forcedReload = async () => {
            await dispatch(loginActions.oneLastSavePendingChanges());
            browserLocation.reload(true);
          };
          setTimeout(forcedReload, 1000);
        } else if (upgradeTokenDuration.asSeconds() > 10) {
          // the user has waited at least 10 seconds and no new token, logout
          forceLogout = true;
        }
      }
      if (forceLogout) {
        await dispatch(loginActions.logoutUserWithSave({
          oneLastSave: true
        }));
        dispatch(postError('Okay', 'There\'s a new version of the TRU Web App!\n\nYou were automatically logged out and the changes will be applied shortly.'));
      }
      if (forceRefresh) {
        const msg = 'There\'s a new version of the TRU Web App!\n\n' + 'Your web application is being updated.';
        let timer = null;
        let ran = false;
        const forcedReload = async () => {
          if (ran) return;
          ran = true;
          clearTimeout(timer);
          await dispatch(loginActions.oneLastSavePendingChanges());
          browserLocation.reload(true);
        };
        timer = setTimeout(forcedReload, 5000);
        dispatch(postError('Okay', msg, forcedReload));
      }
    } else if (error.response.status === 404) {
      // handle bad requests (404)
      const msg = 'We attempted an invalid request.';
      dispatch(postError('Okay', msg));
    } else if (error.response.status === 410) {
      // handle gone reports (410)
      if (handleRedirect !== undefined) {
        handleRedirect(dispatch, getState);
      }
      const msg = error.response.data.error_debug.replace(/DEBUG: /g, '');
      dispatch(postError('Okay', msg));
    } else {
      // handle all other error types
      let msg = 'Unable to successfully process your request: ';
      if (error.response.data) {
        msg += error.response.data.error_msg;
        logger("Error: ".concat(error.response.data.error_debug));
      } else {
        msg += error.response.statusText ? error.response.statusText : '';
      }
      dispatch(postError('Okay', msg));
    }
    dispatch(reset());

    // dispatch failure action
    dispatch(failureAction());
  } else {
    if (error.message === 'Network Error') {
      // catch network errors and post a simple message
      const msg = "".concat(error.message, ":\nThere was a problem reaching the server.");
      // giving the error an id with the date rounded to half a second makes it show multiple
      // errors in quick succession wont flood the screen
      dispatch(postError('Okay', msg, () => {}, "networkError".concat(Math.floor(Date.now() / 500))));
    } else {
      dispatch(postError('Okay', error.message || error));
    }
    dispatch(reset());
    dispatch(failureAction());
  }
  return null;
};
export default {
  increment,
  handleError,
  postError,
  reset,
  retryOnSuccess
};